<!-- components/courses/VideoUploader.vue -->
<template>
  <div class="video-uploader">
    <!-- Toggle between Upload and YouTube Link -->
    <div class="upload-toggle">
      <button
        class="toggle-btn"
        :class="{ active: uploadMethod === 'upload' }"
        @click="uploadMethod = 'upload'"
      >
        📤 Upload Video
      </button>
      <button
        class="toggle-btn"
        :class="{ active: uploadMethod === 'youtube' }"
        @click="uploadMethod = 'youtube'"
      >
        ▶️ YouTube Link
      </button>
    </div>

    <!-- Upload Method -->
    <div
      v-if="uploadMethod === 'upload'"
      class="upload-area"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @click="triggerUpload"
    >
      <div v-if="!uploading && !videoFile">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <h3>Drop your video here</h3>
        <p>or click to browse</p>
        <span class="file-limits">Maximum file size: 100MB</span>
        <span class="supported-formats">Supported: MP4, MOV, AVI, WebM</span>
      </div>

      <div v-else-if="uploading" class="upload-progress">
        <div class="progress-circle">
          <svg viewBox="0 0 36 36">
            <path
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="#e9ecef"
              stroke-width="3"
            />
            <path
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="#FF8000"
              stroke-width="3"
              :stroke-dasharray="`${progress}, 100`"
            />
          </svg>
          <span class="progress-text">{{ Math.round(progress) }}%</span>
        </div>
        <p>Uploading video to Cloudinary...</p>
        <span class="upload-status"
          >Please wait, this may take a few moments</span
        >
        <button class="btn-secondary" @click.stop="cancelUpload">Cancel</button>
      </div>

      <div v-else-if="videoFile && !uploading" class="file-info">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#28a745"
          stroke-width="1.5"
        >
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <h4>{{ videoFile.name }}</h4>
        <p>{{ formatFileSize(videoFile.size) }}</p>
        <div class="file-actions">
          <button
            class="btn-primary"
            @click.stop="startUpload"
            :disabled="uploading"
          >
            Upload to Cloudinary
          </button>
          <button class="btn-secondary" @click.stop="removeFile">Remove</button>
        </div>
      </div>

      <input
        type="file"
        ref="fileInput"
        accept="video/*"
        @change="handleFileSelect"
        style="display: none"
      />
    </div>

    <!-- YouTube Link Method -->
    <div v-else-if="uploadMethod === 'youtube'" class="youtube-section">
      <div class="youtube-input">
        <div class="input-wrapper">
          <span class="youtube-icon">▶️</span>
          <input
            v-model="youtubeUrl"
            type="url"
            placeholder="https://www.youtube.com/watch?v=..."
            class="youtube-url-input"
          />
        </div>
        <div class="youtube-help">
          <p>Enter a valid YouTube video URL</p>
          <span class="hint"
            >Example: https://www.youtube.com/watch?v=dQw4w9WgXcQ</span
          >
        </div>

        <div v-if="youtubeUrl && isValidYouTubeUrl" class="youtube-preview">
          <div class="preview-info">
            <span>✅ Valid YouTube URL</span>
            <span class="video-id"
              >Video ID: {{ extractYouTubeId(youtubeUrl) }}</span
            >
          </div>
          <button
            class="btn-primary"
            @click="confirmYouTubeLink"
            :disabled="!isValidYouTubeUrl"
          >
            Use This Video
          </button>
        </div>
        <div v-else-if="youtubeUrl && !isValidYouTubeUrl" class="youtube-error">
          <span>❌ Invalid YouTube URL. Please check and try again.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { uploadVideoFile, formatFileSize } from "@/services/cloudinary";

const props = defineProps({
  module: {
    type: Object,
    required: true,
  },
  section: {
    type: Object,
    required: true,
  },
  courseId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["uploaded", "cancel"]);

const fileInput = ref(null);
const videoFile = ref(null);
const uploading = ref(false);
const progress = ref(0);
const uploadMethod = ref("upload");
const youtubeUrl = ref("");

// YouTube URL validation
const isValidYouTubeUrl = computed(() => {
  if (!youtubeUrl.value) return false;
  const pattern =
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/watch\?v=[\w-]{11}(&.*)?$|^(https?:\/\/)?(www\.)?youtu\.be\/[\w-]{11}(\?.*)?$/;
  return pattern.test(youtubeUrl.value);
});

const triggerUpload = () => {
  if (!uploading.value) {
    fileInput.value?.click();
  }
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (file.size > 100 * 1024 * 1024) {
      alert("File too large! Maximum 100MB for free Cloudinary plan.");
      return;
    }
    videoFile.value = file;
  }
};

const handleDrop = (event) => {
  const file = event.dataTransfer.files[0];
  if (file) {
    if (file.size > 100 * 1024 * 1024) {
      alert("File too large! Maximum 100MB for free Cloudinary plan.");
      return;
    }
    videoFile.value = file;
  }
};

const startUpload = async () => {
  if (!videoFile.value) return;

  uploading.value = true;
  progress.value = 0;

  try {
    const result = await uploadVideoFile(
      videoFile.value,
      props.courseId,
      props.module.id,
      (p) => {
        progress.value = p;
      },
    );

    if (result.success) {
      const duration = await getVideoDuration(videoFile.value);

      emit("uploaded", {
        url: result.data.url,
        public_id: result.data.public_id,
        streaming_url: result.data.streaming_url || result.data.url,
        duration: duration,
        size: result.data.bytes || videoFile.value.size,
        name: videoFile.value.name,
        type: videoFile.value.type,
        source: "upload",
      });

      videoFile.value = null;
    } else {
      alert("Upload failed: " + result.error);
    }
  } catch (error) {
    console.error("Upload error:", error);
    alert("Upload failed: " + error.message);
  } finally {
    uploading.value = false;
  }
};

const getVideoDuration = (file) => {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      const minutes = Math.floor(video.duration / 60);
      const seconds = Math.floor(video.duration % 60);
      resolve(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };
    video.onerror = () => {
      resolve("0:00");
    };
    video.src = URL.createObjectURL(file);
  });
};

const extractYouTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([\w-]{11})(?:&|$)/,
    /(?:youtu\.be\/)([\w-]{11})(?:\?|$)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const confirmYouTubeLink = () => {
  if (!isValidYouTubeUrl.value) return;

  const videoId = extractYouTubeId(youtubeUrl.value);
  const embedUrl = `https://www.youtube.com/embed/${videoId}`;
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;

  emit("uploaded", {
    url: watchUrl,
    embed_url: embedUrl,
    video_id: videoId,
    source: "youtube",
    name: "YouTube Video",
    type: "youtube",
    duration: "N/A",
    size: 0,
  });

  youtubeUrl.value = "";
};

const cancelUpload = () => {
  uploading.value = false;
  videoFile.value = null;
  progress.value = 0;
};

const removeFile = () => {
  videoFile.value = null;
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.video-uploader {
  .upload-toggle {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    background: #f8f9fa;
    padding: 4px;
    border-radius: 8px;

    .toggle-btn {
      flex: 1;
      padding: 10px 16px;
      border: none;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      background: transparent;
      color: #868e96;

      &:hover {
        color: #1a1a1a;
      }

      &.active {
        background: #fff;
        color: $primary-orange;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }
    }
  }

  .upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 12px;
    padding: 40px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: $primary-orange;
      background: rgba(255, 128, 0, 0.05);
    }

    svg {
      color: #adb5bd;
    }

    h3 {
      margin: 12px 0 4px 0;
      color: #1a1a1a;
    }

    p {
      margin: 4px 0;
      color: #868e96;
    }

    .file-limits,
    .supported-formats {
      display: block;
      font-size: 12px;
      color: #adb5bd;
      margin-top: 4px;
    }

    .upload-progress {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .progress-circle {
        position: relative;
        width: 80px;
        height: 80px;

        svg {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);
        }

        .progress-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 18px;
          font-weight: 700;
          color: $primary-orange;
        }
      }

      .upload-status {
        font-size: 13px;
        color: #868e96;
      }

      .btn-secondary {
        padding: 8px 16px;
        background: #e9ecef;
        border: none;
        border-radius: 4px;
        color: #495057;
        cursor: pointer;
        font-weight: 500;

        &:hover {
          background: #dee2e6;
        }
      }
    }

    .file-info {
      h4 {
        margin: 8px 0 4px 0;
        color: #1a1a1a;
      }

      p {
        margin: 0 0 12px 0;
        color: #868e96;
      }

      .file-actions {
        display: flex;
        gap: 8px;
        justify-content: center;

        .btn-primary {
          padding: 8px 24px;
          background: $primary-orange;
          color: #fff;
          border: none;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;

          &:hover:not(:disabled) {
            background: darken($primary-orange, 10%);
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }

        .btn-secondary {
          padding: 8px 24px;
          background: #e9ecef;
          border: none;
          border-radius: 4px;
          color: #495057;
          cursor: pointer;

          &:hover {
            background: #dee2e6;
          }
        }
      }
    }
  }

  .youtube-section {
    .youtube-input {
      .input-wrapper {
        display: flex;
        align-items: center;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        padding: 4px;
        transition: all 0.3s ease;
        background: #fff;

        &:focus-within {
          border-color: $primary-orange;
          box-shadow: 0 0 0 4px rgba(255, 128, 0, 0.1);
        }

        .youtube-icon {
          font-size: 24px;
          padding: 0 12px;
          color: #ff0000;
        }

        .youtube-url-input {
          flex: 1;
          padding: 12px;
          border: none;
          font-size: 14px;
          outline: none;
          background: transparent;

          &::placeholder {
            color: #adb5bd;
          }
        }
      }

      .youtube-help {
        margin-top: 8px;

        p {
          margin: 0;
          font-size: 14px;
          color: #495057;
        }

        .hint {
          display: block;
          font-size: 12px;
          color: #868e96;
          margin-top: 4px;
        }
      }

      .youtube-preview {
        margin-top: 16px;
        padding: 16px;
        background: #f8f9fa;
        border-radius: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;

        .preview-info {
          display: flex;
          flex-direction: column;
          gap: 4px;

          span {
            font-size: 14px;

            &.video-id {
              font-size: 12px;
              color: #868e96;
            }
          }
        }

        .btn-primary {
          padding: 8px 24px;
          background: $primary-orange;
          color: #fff;
          border: none;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;

          &:hover:not(:disabled) {
            background: darken($primary-orange, 10%);
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }

      .youtube-error {
        margin-top: 12px;
        padding: 12px;
        background: #fff0f0;
        color: #dc3545;
        border-radius: 8px;
        font-size: 14px;
        border: 1px solid #ffd4d4;
      }
    }
  }
}
</style>
